import React, { createContext, useContext, useReducer, useCallback } from 'react';
import {
  VolunteerShift,
  VolunteerShiftRegistration,
  VolunteerProfile,
  VolunteerStatusResponse,
  VolunteerApplicationData,
} from '../types/volunteer';
import * as volunteerApi from '../services/volunteerApi';

interface VolunteerState {
  status: VolunteerStatusResponse | null;
  profile: VolunteerProfile | null;
  shifts: VolunteerShift[];
  myShifts: VolunteerShiftRegistration[];
  loading: boolean;
  error: string | null;
}

type VolunteerAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_STATUS'; payload: VolunteerStatusResponse }
  | { type: 'SET_PROFILE'; payload: VolunteerProfile }
  | { type: 'SET_SHIFTS'; payload: VolunteerShift[] }
  | { type: 'SET_MY_SHIFTS'; payload: VolunteerShiftRegistration[] };

const initialState: VolunteerState = {
  status: null,
  profile: null,
  shifts: [],
  myShifts: [],
  loading: false,
  error: null,
};

function volunteerReducer(state: VolunteerState, action: VolunteerAction): VolunteerState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_STATUS':
      return { ...state, status: action.payload, loading: false };
    case 'SET_PROFILE':
      return { ...state, profile: action.payload, loading: false };
    case 'SET_SHIFTS':
      return { ...state, shifts: action.payload, loading: false };
    case 'SET_MY_SHIFTS':
      return { ...state, myShifts: action.payload, loading: false };
    default:
      return state;
  }
}

interface VolunteerContextValue extends VolunteerState {
  fetchStatus: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  fetchShifts: (params?: Record<string, string>) => Promise<void>;
  fetchMyShifts: (hidePast?: boolean) => Promise<void>;
  apply: (data: VolunteerApplicationData) => Promise<void>;
  registerForShift: (shiftId: string, roleId?: string) => Promise<void>;
  cancelShift: (shiftId: string) => Promise<void>;
  addSkill: (name: string) => Promise<void>;
  removeSkill: (skillId: string) => Promise<void>;
}

const VolunteerContext = createContext<VolunteerContextValue | undefined>(undefined);

export const VolunteerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(volunteerReducer, initialState);

  const fetchStatus = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { data } = await volunteerApi.getVolunteerStatus();
      dispatch({ type: 'SET_STATUS', payload: data });
    } catch (err: any) {
      dispatch({ type: 'SET_ERROR', payload: err.response?.data?.error || 'Failed to fetch status' });
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { data } = await volunteerApi.getVolunteerProfile();
      dispatch({ type: 'SET_PROFILE', payload: data });
    } catch (err: any) {
      dispatch({ type: 'SET_ERROR', payload: err.response?.data?.error || 'Failed to fetch profile' });
    }
  }, []);

  const fetchShifts = useCallback(async (params?: Record<string, string>) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { data } = await volunteerApi.getAvailableShifts(params);
      dispatch({ type: 'SET_SHIFTS', payload: data });
    } catch (err: any) {
      dispatch({ type: 'SET_ERROR', payload: err.response?.data?.error || 'Failed to fetch shifts' });
    }
  }, []);

  const fetchMyShifts = useCallback(async (hidePast?: boolean) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { data } = await volunteerApi.getMyShifts(hidePast);
      dispatch({ type: 'SET_MY_SHIFTS', payload: data });
    } catch (err: any) {
      dispatch({ type: 'SET_ERROR', payload: err.response?.data?.error || 'Failed to fetch shifts' });
    }
  }, []);

  const apply = useCallback(async (applicationData: VolunteerApplicationData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await volunteerApi.applyAsVolunteer(applicationData);
      dispatch({ type: 'SET_STATUS', payload: {
        isVolunteer: false,
        volunteerStatus: 'pending',
        volunteerAvailability: applicationData.availability,
        volunteerExperience: applicationData.experience,
        volunteerAppliedAt: new Date().toISOString(),
      }});
    } catch (err: any) {
      dispatch({ type: 'SET_ERROR', payload: err.response?.data?.error || 'Failed to submit application' });
      throw err;
    }
  }, []);

  const registerForShift = useCallback(async (shiftId: string, roleId?: string) => {
    try {
      await volunteerApi.registerForShift(shiftId, roleId);
    } catch (err: any) {
      throw err;
    }
  }, []);

  const cancelShift = useCallback(async (shiftId: string) => {
    try {
      await volunteerApi.cancelShiftRegistration(shiftId);
    } catch (err: any) {
      throw err;
    }
  }, []);

  const addSkillFn = useCallback(async (name: string) => {
    try {
      await volunteerApi.addSkill(name);
    } catch (err: any) {
      throw err;
    }
  }, []);

  const removeSkillFn = useCallback(async (skillId: string) => {
    try {
      await volunteerApi.removeSkill(skillId);
    } catch (err: any) {
      throw err;
    }
  }, []);

  const value: VolunteerContextValue = {
    ...state,
    fetchStatus,
    fetchProfile,
    fetchShifts,
    fetchMyShifts,
    apply,
    registerForShift,
    cancelShift,
    addSkill: addSkillFn,
    removeSkill: removeSkillFn,
  };

  return (
    <VolunteerContext.Provider value={value}>
      {children}
    </VolunteerContext.Provider>
  );
};

export const useVolunteer = () => {
  const context = useContext(VolunteerContext);
  if (!context) {
    throw new Error('useVolunteer must be used within a VolunteerProvider');
  }
  return context;
};
